/**
 * CommandFormWizard.jsx
 * Formulario de Comando Salvo em Etapas Premium
 *
 * 2 Passos:
 * 1. Basico - Descricao, Tipo, Parametros (obrigatorio)
 * 2. Revisao
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import {
  Terminal as CommandIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { prefixString } from '../../common/util/stringUtils';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import BaseCommandView from './BaseCommandView';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  // Create a wrapper setItem for BaseCommandView compatibility
  const setItem = (newData) => {
    // Merge all changes
    const updates = {};
    if (newData.type !== undefined) updates.type = newData.type;
    if (newData.attributes !== undefined) updates.attributes = newData.attributes;
    if (newData.textChannel !== undefined) updates.textChannel = newData.textChannel;
    updateData(updates);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BaseCommandView
          item={data}
          setItem={setItem}
        />
      </Box>

      {errors?.type && (
        <Typography color="error" variant="caption">
          {errors.type}
        </Typography>
      )}
    </Box>
  );
};

// ============================================================================
// STEP 2: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const attributeCount = data.attributes ? Object.keys(data.attributes).length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />
        <InfoRow label={t('sharedType')} value={data.type ? t(prefixString('command', data.type)) : '-'} />
        <InfoRow label={t('commandSendSms')} value={data.textChannel ? t('sharedYes') : t('sharedNo')} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount}` : '-'} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CommandFormWizard = ({ command, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardCommandBasicDescription') || 'Dados do comando',
      icon: <CommandIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.type) errors.type = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedType'), value: data.type ? t(prefixString('command', data.type)) : null },
      ],
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = command?.id ? `/api/commands/${command.id}` : '/api/commands';
      const method = command?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        onSave?.(saved);
        navigate('/settings/commands');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/commands');
  };

  const initialData = command || {
    description: '',
    type: '',
    textChannel: false,
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={command?.id ? null : 'command-wizard-draft'}
      completeButtonText={command?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      showSummary
    />
  );
};

export default CommandFormWizard;
